import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ConnectionState } from '@bb/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { count, utcClock, utcDate } from '@/lib/format';
import { useLive, type LinkState } from '@/lib/live';
import { StatusDot, type DotTone } from '@/components/ui/StatusDot';

function useUtcClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

const INGEST_TONE: Record<ConnectionState, DotTone> = {
  CONNECTED: 'ok',
  CONNECTING: 'warn',
  DISCONNECTED: 'alert',
  ERROR: 'alert',
  IDLE: 'idle',
};

const LINK_TONE: Record<LinkState, DotTone> = {
  open: 'ok',
  connecting: 'warn',
  closed: 'alert',
};

function Readout({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="label-xs">{label}</span>
      <span className={`data-mono text-[11px] ${tone ?? 'text-ink'}`}>{value}</span>
    </div>
  );
}

export function StatusBar() {
  const now = useUtcClock();
  const { linkState, ingestion } = useLive();

  const status = useQuery({
    queryKey: queryKeys.status,
    queryFn: () => api.system.status(),
    // The stream carries live changes; this is only for the aggregate counters.
    refetchInterval: 60_000,
  });

  const ingestState = ingestion?.state ?? status.data?.ingestion.state ?? 'IDLE';
  const mode = ingestion?.mode ?? status.data?.ingestion.mode ?? '—';
  const message = ingestion?.message ?? status.data?.ingestion.message ?? null;

  return (
    <header className="flex h-12 shrink-0 items-center gap-5 border-b border-line bg-panel/70 px-4 backdrop-blur-[1px]">
      <div className="flex items-center gap-2">
        <StatusDot tone={LINK_TONE[linkState]} pulse={linkState !== 'open'} />
        <span className="label-xs">Link</span>
        <span className="data-mono text-[11px] uppercase text-ink">{linkState}</span>
      </div>

      <div className="h-4 w-px bg-line" />

      <div className="flex items-center gap-2" title={message ?? undefined}>
        <StatusDot tone={INGEST_TONE[ingestState]} pulse={ingestState === 'CONNECTING'} />
        <span className="label-xs">Ingest</span>
        <span className="data-mono text-[11px] uppercase text-ink">
          {ingestState}
          <span className="text-ink-faint"> / {mode}</span>
        </span>
      </div>

      <div className="ml-auto flex items-center gap-5">
        <Readout
          label="Targets"
          value={
            status.data ? `${status.data.targets.active}/${status.data.targets.total}` : '—'
          }
        />
        <Readout
          label="Today"
          value={status.data ? count(status.data.posts.today) : '—'}
          tone="text-accent"
        />
        <Readout
          label="Unread"
          value={status.data ? count(status.data.posts.unread) : '—'}
          tone={status.data && status.data.posts.unread > 0 ? 'text-amber' : 'text-ink-dim'}
        />

        <div className="h-4 w-px bg-line" />

        <div className="flex items-baseline gap-2">
          <span className="data-mono text-[11px] text-ink-faint">{utcDate(now)}</span>
          <span className="data-mono text-[11px] text-ink">{utcClock(now)}</span>
          <span className="label-xs">UTC</span>
        </div>
      </div>
    </header>
  );
}
